import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { FaStethoscope, FaExclamationTriangle, FaHistory } from 'react-icons/fa';
import diseaseDiagnosisApi from '../../../services/diseaseDiagnosisApi';
import { ChartCard, doughnutOptions } from '../analytics/analyticsShared';

const EMPTY_FORM = {
  symptomsText: '',
  ageDays: '',
  breed: '',
  vaccinationStatus: '',
  mortalityRate: '',
  weather: '',
  feedType: '',
  waterIntake: '',
  flockSize: '',
  additionalNotes: '',
};

const RISK_COLORS = {
  low: 'bg-green-50 text-green-700 ring-green-200',
  moderate: 'bg-amber-50 text-amber-700 ring-amber-200',
  high: 'bg-orange-50 text-orange-700 ring-orange-200',
  critical: 'bg-red-50 text-red-700 ring-red-200',
  unknown: 'bg-gray-50 text-gray-500 ring-gray-200',
};

// A real AI diagnosis tool: symptoms + flock context go to OpenAI, and the
// full structured response (plus the raw model output) is permanently
// stored server-side in DiseaseDiagnosisReport — this page just submits
// and displays it.
export default function AiDiseaseDiagnosis() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const res = await diseaseDiagnosisApi.getMine();
      setHistory(res.reports || []);
    } catch (err) {
      /* interceptor toasts */
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const riskChartData = useMemo(() => {
    const colors = { low: '#1e5c33', moderate: '#d97706', high: '#ea580c', critical: '#dc2626', unknown: '#6b7280' };
    const keys = ['low', 'moderate', 'high', 'critical', 'unknown'];
    const counts = keys.map((k) => history.filter((h) => h.riskLevel === k).length);
    const entries = keys.map((k, i) => ({ k, c: counts[i] })).filter((x) => x.c > 0);
    return {
      labels: entries.map((x) => x.k.charAt(0).toUpperCase() + x.k.slice(1)),
      datasets: [{ data: entries.map((x) => x.c), backgroundColor: entries.map((x) => colors[x.k]), borderWidth: 0 }],
    };
  }, [history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const symptoms = form.symptomsText.split(',').map((s) => s.trim()).filter(Boolean);
    if (symptoms.length === 0) {
      setError('Enter at least one symptom, separated by commas.');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await diseaseDiagnosisApi.diagnose({
        symptoms,
        ageDays: form.ageDays ? Number(form.ageDays) : undefined,
        breed: form.breed || undefined,
        vaccinationStatus: form.vaccinationStatus || undefined,
        mortalityRate: form.mortalityRate ? Number(form.mortalityRate) : undefined,
        weather: form.weather || undefined,
        feedType: form.feedType || undefined,
        waterIntake: form.waterIntake || undefined,
        flockSize: form.flockSize ? Number(form.flockSize) : undefined,
        additionalNotes: form.additionalNotes || undefined,
      });
      setResult(res.report);
      loadHistory();
    } catch (err) {
      setError(typeof err === 'string' ? err : err?.error || 'Diagnosis failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaStethoscope className="text-primary" /> AI Disease Diagnosis
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            Real AI analysis powered by OpenAI. Every submission is permanently recorded.
          </p>
        </div>
        <button
          onClick={() => setShowHistory((s) => !s)}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <FaHistory /> {showHistory ? 'Hide' : 'View'} my history ({history.length})
        </button>
      </div>

      {showHistory && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {history.length > 3 && (
            <ChartCard title="Risk level breakdown" className="h-56 md:order-2">
              <div className="h-40">
                <Doughnut data={riskChartData} options={doughnutOptions} />
              </div>
            </ChartCard>
          )}
          <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3 max-h-72 overflow-y-auto ${history.length > 3 ? 'md:order-1' : 'md:col-span-2'}`}>
            {history.length === 0 ? (
              <p className="text-sm text-gray-400">No past diagnoses yet.</p>
            ) : (
              history.map((r) => (
                <div key={r._id} className="text-sm border-b border-gray-50 pb-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${RISK_COLORS[r.riskLevel] || RISK_COLORS.unknown}`}>
                      {r.riskLevel}
                    </span>
                    <span className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="text-gray-600 mt-1">
                    {r.possibleDiseases?.map((d) => d.name).join(', ') || 'No diseases identified'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Symptoms (comma-separated) *</label>
          <input
            required
            value={form.symptomsText}
            onChange={(e) => setForm({ ...form, symptomsText: e.target.value })}
            placeholder="e.g. lethargy, diarrhea, drop in egg production, gasping"
            className="w-full border border-gray-200 rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Flock age (days)</label>
          <input type="number" min="0" value={form.ageDays} onChange={(e) => setForm({ ...form, ageDays: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Breed</label>
          <input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Vaccination status</label>
          <input value={form.vaccinationStatus} onChange={(e) => setForm({ ...form, vaccinationStatus: e.target.value })} placeholder="e.g. Newcastle done, IBD pending" className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Recent mortality rate (%)</label>
          <input type="number" min="0" max="100" value={form.mortalityRate} onChange={(e) => setForm({ ...form, mortalityRate: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Flock size</label>
          <input type="number" min="0" value={form.flockSize} onChange={(e) => setForm({ ...form, flockSize: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Weather / housing conditions</label>
          <input value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Feed type</label>
          <input value={form.feedType} onChange={(e) => setForm({ ...form, feedType: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Water intake</label>
          <input value={form.waterIntake} onChange={(e) => setForm({ ...form, waterIntake: e.target.value })} placeholder="e.g. normal, reduced by half since yesterday" className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Additional notes</label>
          <textarea value={form.additionalNotes} onChange={(e) => setForm({ ...form, additionalNotes: e.target.value })} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
        </div>

        {error && <div className="md:col-span-2 text-sm text-red-600">{error}</div>}

        <div className="md:col-span-2 flex justify-end">
          <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-lg bg-primary text-white text-sm disabled:opacity-60">
            {submitting ? 'Analysing…' : 'Get AI diagnosis'}
          </button>
        </div>
      </form>

      {result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ring-1 ring-inset ${RISK_COLORS[result.riskLevel] || RISK_COLORS.unknown}`}>
              Risk: {result.riskLevel}
            </span>
            {result.veterinaryConsultationRequired && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">
                <FaExclamationTriangle /> Veterinary consultation required — {result.veterinaryUrgency}
              </span>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Possible diseases</h3>
            <div className="space-y-2">
              {result.possibleDiseases?.map((d, i) => (
                <div key={i} className="flex items-start justify-between border-b border-gray-50 pb-2">
                  <div>
                    <div className="font-medium text-gray-700">{d.name}</div>
                    <div className="text-xs text-gray-500">{d.reasoning}</div>
                  </div>
                  {d.confidence != null && <div className="text-sm font-semibold text-primary">{Math.round(d.confidence)}%</div>}
                </div>
              ))}
            </div>
          </div>

          {result.recommendations?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 mb-1">Recommendations</h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                {result.recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 mb-1">Isolation advice</div>
              <div className="text-gray-700">{result.isolationAdvice || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 mb-1">Vaccination advice</div>
              <div className="text-gray-700">{result.vaccinationAdvice || '—'}</div>
            </div>
          </div>

          <p className="text-xs text-gray-400 italic">{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
